import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import './AnalyticsDashboardPage.css';

interface DashboardStats {
  totalTestCases: number;
  totalScenarios: number;
  totalProjects: number;
  totalRuns: number;
  completedRuns: number;
  passCount: number;
  failCount: number;
  blockedCount: number;
  successRate: number;
}

interface ActivityRow {
  sort_key: string;
  period: string;
  user: string;
  tc_created: number;
  sc_created: number;
  runs_created: number;
  executions: number;
}

const PRIORITY_COLORS: Record<string, string> = {
  Critical: '#ef4444',
  High: '#f97316',
  Medium: '#3b82f6',
  Low: '#9ca3af',
};

export const AnalyticsDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'Admin';

  const [stats, setStats] = useState<DashboardStats>({
    totalTestCases: 0, totalScenarios: 0, totalProjects: 0,
    totalRuns: 0, completedRuns: 0,
    passCount: 0, failCount: 0, blockedCount: 0, successRate: 0,
  });
  const [recentRuns, setRecentRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Activity report state
  const [actPeriod, setActPeriod] = useState<'weekly' | 'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [actView, setActView] = useState<'overall' | 'by_user'>('overall');
  const [actMonths, setActMonths] = useState(12);
  const [actRows, setActRows] = useState<ActivityRow[]>([]);
  const [actUsers, setActUsers] = useState<string[]>([]);
  const [actUserFilter, setActUserFilter] = useState<string>('');
  const [actLoading, setActLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Global date range filter
  const DATE_RANGES = [
    { label: 'Last 30 Days', days: 30 },
    { label: '1 Month',      days: 30 },
    { label: '3 Months',     days: 90 },
    { label: '6 Months',     days: 180 },
    { label: '1 Year',       days: 365 },
  ];
  const [dateRangeDays, setDateRangeDays] = useState(30);
  const [dateRangeLabel, setDateRangeLabel] = useState('Last 30 Days');
  const [showDateDropdown, setShowDateDropdown] = useState(false);

  useEffect(() => { loadDashboard(); }, [dateRangeDays]); // eslint-disable-line
  useEffect(() => { loadActivityReport(); }, [actPeriod, actView, actMonths]); // eslint-disable-line

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const resp = await apiClient.get(`/api/v1/dashboard/stats?days=${dateRangeDays}`);
      const data = resp.data as any;
      const kpis = data.kpis;
      const exec = data.execution_summary;
      setStats({
        totalTestCases: kpis.total_test_cases,
        totalScenarios: kpis.total_scenarios,
        totalProjects: kpis.total_projects,
        totalRuns: kpis.total_runs,
        completedRuns: kpis.completed_runs,
        passCount: exec.pass_count,
        failCount: exec.fail_count,
        blockedCount: exec.blocked_count,
        successRate: exec.success_rate,
      });
      setRecentRuns(data.recent_runs || []);
    } catch (e) {
      console.error('Dashboard load error', e);
    } finally {
      setLoading(false);
    }
  };

  const loadActivityReport = useCallback(async () => {
    setActLoading(true);
    try {
      const resp = await apiClient.get(
        `/api/v1/dashboard/activity-report?period=${actPeriod}&view=${actView}&months_back=${actMonths}`
      );
      const data = resp.data as any;
      setActRows(data.rows || []);
      setActUsers(data.users || []);
      setActUserFilter('');
    } catch (e) {
      console.error('Activity report error', e);
    } finally {
      setActLoading(false);
    }
  }, [actPeriod, actView, actMonths]);

  const handleExport = async () => {
    if (!isAdmin) return;
    setExportLoading(true);
    try {
      const authTokens = localStorage.getItem('auth_tokens');
      const tokens = authTokens ? JSON.parse(authTokens) : null;
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      const resp = await fetch(
        `${apiBase}/api/v1/dashboard/export-report?period=${actPeriod}&months_back=${actMonths}`,
        { headers: { Authorization: `Bearer ${tokens?.access_token}` } }
      );
      if (!resp.ok) throw new Error('Export failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assurex_report_${actPeriod}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error('Export error', e);
    } finally {
      setExportLoading(false);
    }
  };

  const formatDuration = (run: any) => {
    if (!run.duration_seconds) return '—';
    const m = Math.floor(run.duration_seconds / 60);
    const s = run.duration_seconds % 60;
    return `${m}m ${s.toString().padStart(2, '0')}s`;
  };

  const timeAgo = (dateStr: string) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return 'just now';
    if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`;
    const d = Math.floor(h / 24);
    return `${d} day${d > 1 ? 's' : ''} ago`;
  };

  const statusBadge = (status: string, pct?: number) => {
    if (status === 'COMPLETED') return <span className="run-badge badge-completed">COMPLETED</span>;
    if (status === 'IN_PROGRESS') return <span className="run-badge badge-inprogress">IN PROGRESS</span>;
    if (status === 'ABORTED') return <span className="run-badge badge-failed" style={{ color: pct && pct < 80 ? '#dc2626' : undefined }}>FAILED</span>;
    return <span className="run-badge badge-notstarted">NOT STARTED</span>;
  };

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="dashboard-page">
      {/* Page header */}
      <div className="dash-header">
        <div>
          <h1 className="dash-title">Analytics Dashboard</h1>
          <p className="dash-subtitle">
            {greeting()}, {firstName}. Real-time quality insights across all enterprise test cycles.
          </p>
        </div>
        <div className="dash-header-actions">
          {/* Date range dropdown */}
          <div className="date-range-wrap" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowDateDropdown(false); }}>
            <button
              className="dash-btn-outline date-range-btn"
              onClick={() => setShowDateDropdown(v => !v)}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              {dateRangeLabel}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 2 }}>
                <path d="M6 9l6 6 6-6"/>
              </svg>
            </button>
            {showDateDropdown && (
              <div className="date-range-dropdown">
                {DATE_RANGES.map(opt => (
                  <button
                    key={opt.label}
                    className={`date-range-option${dateRangeLabel === opt.label ? ' selected' : ''}`}
                    onClick={() => {
                      setDateRangeDays(opt.days);
                      setDateRangeLabel(opt.label);
                      setShowDateDropdown(false);
                    }}
                  >
                    {dateRangeLabel === opt.label && (
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            className={`dash-btn-primary${!isAdmin ? ' dash-btn-disabled' : ''}`}
            onClick={handleExport}
            disabled={!isAdmin || exportLoading}
            title={isAdmin ? 'Export report as CSV' : 'Only Admin users can export reports'}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {exportLoading ? 'Exporting...' : 'Export Data'}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="dash-loading">Loading dashboard...</div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="kpi-grid">
            <div className="kpi-card">
              <div className="kpi-top">
                <div className="kpi-icon kpi-icon-blue">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <span className={`kpi-delta ${stats.successRate >= 90 ? 'delta-up' : 'delta-down'}`}>
                  {stats.successRate >= 90 ? '+' : ''}{stats.successRate > 0 ? `${stats.successRate}%` : 'N/A'}
                </span>
              </div>
              <div className="kpi-label">OVERALL TEST SUCCESS RATE</div>
              <div className="kpi-value">{stats.successRate > 0 ? `${stats.successRate}%` : '—'}</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-top">
                <div className="kpi-icon kpi-icon-purple">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/>
                  </svg>
                </div>
                <span className="kpi-delta delta-neutral">STABLE</span>
              </div>
              <div className="kpi-label">TOTAL TEST CASES</div>
              <div className="kpi-value">{stats.totalTestCases.toLocaleString()}</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-top">
                <div className="kpi-icon kpi-icon-red">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <span className="kpi-delta delta-down">{stats.failCount > 0 ? `-${stats.failCount}` : '0'}</span>
              </div>
              <div className="kpi-label">FAILED EXECUTIONS</div>
              <div className="kpi-value">{stats.failCount}</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-top">
                <div className="kpi-icon kpi-icon-teal">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
                  </svg>
                </div>
                <span className="kpi-delta delta-up">+{stats.totalProjects}</span>
              </div>
              <div className="kpi-label">TOTAL PROJECTS</div>
              <div className="kpi-value">{stats.totalProjects}</div>
            </div>
          </div>

          {/* Middle row */}
          <div className="dash-mid-row">
            {/* Execution summary chart */}
            <div className="dash-card dash-card-wide">
              <div className="dash-card-header">
                <div>
                  <h3>Test Execution Summary</h3>
                  <p>Pass / Fail / Blocked across all completed runs</p>
                </div>
                <div className="chart-legend">
                  <span className="legend-dot" style={{ background: '#22c55e' }}></span> Pass
                  <span className="legend-dot" style={{ background: '#ef4444', marginLeft: 12 }}></span> Fail
                  <span className="legend-dot" style={{ background: '#f59e0b', marginLeft: 12 }}></span> Blocked
                </div>
              </div>
              <div className="exec-bars">
                {[
                  { label: 'Pass', count: stats.passCount, color: '#22c55e' },
                  { label: 'Fail', count: stats.failCount, color: '#ef4444' },
                  { label: 'Blocked', count: stats.blockedCount, color: '#f59e0b' },
                ].map(item => {
                  const total = stats.passCount + stats.failCount + stats.blockedCount;
                  const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  return (
                    <div key={item.label} className="exec-bar-row">
                      <span className="exec-bar-label">{item.label}</span>
                      <div className="exec-bar-track">
                        <div className="exec-bar-fill" style={{ width: `${pct}%`, background: item.color }} />
                      </div>
                      <span className="exec-bar-count">{item.count.toLocaleString()}</span>
                      <span className="exec-bar-pct">{pct}%</span>
                    </div>
                  );
                })}
                {stats.passCount + stats.failCount + stats.blockedCount === 0 && (
                  <div className="exec-empty">No completed test runs yet. <Link to="/test-runs/create">Create a test run</Link> to see results here.</div>
                )}
              </div>
              <div className="exec-totals">
                <div className="exec-total-item">
                  <span className="exec-total-num">{stats.totalRuns}</span>
                  <span className="exec-total-lbl">Total Runs</span>
                </div>
                <div className="exec-total-item">
                  <span className="exec-total-num">{stats.completedRuns}</span>
                  <span className="exec-total-lbl">Completed</span>
                </div>
                <div className="exec-total-item">
                  <span className="exec-total-num">{stats.totalRuns - stats.completedRuns}</span>
                  <span className="exec-total-lbl">In Progress / Pending</span>
                </div>
              </div>
            </div>

            {/* Activity Report */}
            <div className="dash-card">
              <div className="dash-card-header">
                <div>
                  <h3>Activity Report</h3>
                  <p>Creation &amp; execution counts by period</p>
                </div>
              </div>

              {/* Controls */}
              <div className="act-controls">
                <div className="act-control-group">
                  <label>Period</label>
                  <select value={actPeriod} onChange={e => setActPeriod(e.target.value as any)} className="act-select">
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div className="act-control-group">
                  <label>View</label>
                  <select value={actView} onChange={e => setActView(e.target.value as any)} className="act-select">
                    <option value="overall">Overall</option>
                    <option value="by_user">By User</option>
                  </select>
                </div>
                <div className="act-control-group">
                  <label>Range</label>
                  <select value={actMonths} onChange={e => setActMonths(Number(e.target.value))} className="act-select">
                    <option value={3}>3 months</option>
                    <option value={6}>6 months</option>
                    <option value={12}>12 months</option>
                    <option value={24}>24 months</option>
                  </select>
                </div>
                {actView === 'by_user' && actUsers.length > 0 && (
                  <div className="act-control-group">
                    <label>User</label>
                    <select value={actUserFilter} onChange={e => setActUserFilter(e.target.value)} className="act-select">
                      <option value="">All</option>
                      {actUsers.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Table */}
              {actLoading ? (
                <div className="act-loading">Loading...</div>
              ) : actRows.length === 0 ? (
                <div className="act-empty">No activity data for this period.</div>
              ) : (
                <div className="act-table-wrap">
                  <table className="act-table">
                    <thead>
                      <tr>
                        <th>Period</th>
                        {actView === 'by_user' && <th>User</th>}
                        <th>TC Created</th>
                        <th>SC Created</th>
                        <th>Runs</th>
                        <th>Executions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actRows
                        .filter(r => !actUserFilter || r.user === actUserFilter)
                        .map((row, i) => (
                          <tr key={i}>
                            <td className="act-period">{row.period}</td>
                            {actView === 'by_user' && <td className="act-user">{row.user}</td>}
                            <td><span className="act-num act-tc">{row.tc_created}</span></td>
                            <td><span className="act-num act-sc">{row.sc_created}</span></td>
                            <td><span className="act-num act-run">{row.runs_created}</span></td>
                            <td><span className="act-num act-exec">{row.executions}</span></td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Bottom row */}
          <div className="dash-bottom-row">
            {/* Recent test runs */}
            <div className="dash-card dash-card-wide">
              <div className="dash-card-header">
                <div>
                  <h3>Recent Test Runs</h3>
                </div>
                <Link to="/test-runs" className="dash-view-all">View All →</Link>
              </div>
              {recentRuns.length === 0 ? (
                <div className="exec-empty">No test runs yet. <Link to="/test-runs/create">Create your first test run</Link>.</div>
              ) : (
                <table className="runs-table">
                  <thead>
                    <tr>
                      <th>RUN ID / NAME</th>
                      <th>STATUS</th>
                      <th>SUCCESS RATE</th>
                      <th>DURATION</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRuns.map(run => (
                      <tr key={run.id}>
                        <td>
                          <Link to={`/test-runs/${run.id}`} className="run-name">{run.name}</Link>
                          <div className="run-meta">#{run.id.slice(0, 6)} · {timeAgo(run.created_at)}</div>
                        </td>
                        <td>{statusBadge(run.status, run.pass_percentage)}</td>
                        <td>
                          <span className={run.pass_percentage < 80 ? 'run-rate-fail' : 'run-rate-pass'}>
                            {run.pass_percentage != null ? `${Math.round(run.pass_percentage * 10) / 10}%` : '—'}
                          </span>
                        </td>
                        <td className="run-duration">{formatDuration(run)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Quick stats */}
            <div className="dash-card">
              <div className="dash-card-header">
                <h3>Quick Stats</h3>
              </div>
              <div className="quick-stats">
                {[
                  { label: 'Projects', value: stats.totalProjects, icon: '📁', link: '/' },
                  { label: 'Test Cases', value: stats.totalTestCases, icon: '✅', link: '/test-cases' },
                  { label: 'Test Scenarios', value: stats.totalScenarios, icon: '🔬', link: '/test-scenarios' },
                  { label: 'Test Runs', value: stats.totalRuns, icon: '▶️', link: '/test-runs' },
                ].map(item => (
                  <Link key={item.label} to={item.link} className="quick-stat-item">
                    <span className="quick-stat-icon">{item.icon}</span>
                    <div className="quick-stat-info">
                      <span className="quick-stat-value">{item.value.toLocaleString()}</span>
                      <span className="quick-stat-label">{item.label}</span>
                    </div>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </Link>
                ))}
              </div>
              <div className="dash-card-header" style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #f3f4f6' }}>
                <h3>Priority Breakdown</h3>
              </div>
              <div className="priority-pills">
                {Object.entries(PRIORITY_COLORS).map(([p, c]) => (
                  <div key={p} className="priority-pill">
                    <span className="priority-dot" style={{ background: c }}></span>
                    <span className="priority-pill-label">{p}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
